import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { COUNTRIES, PINNED_COUNTRY_COUNT } from '../../data/countries';

export interface CountrySelectProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  success?: boolean;
  helperText?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  id?: string;
  name?: string;
}

export const CountrySelect = ({
  label,
  value,
  onChange,
  error,
  success,
  helperText,
  placeholder = 'Select a country',
  required = false,
  disabled = false,
  fullWidth = false,
  id,
  name = 'country',
}: CountrySelectProps) => {
  const generatedId = useId();
  const inputId = id || `country-${generatedId}`;
  const listboxId = `${inputId}-listbox`;

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const filtered = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) {
      return COUNTRIES;
    }
    return COUNTRIES.filter((c) => c.toLowerCase().includes(trimmed));
  }, [query]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !listRef.current) {
      return;
    }
    const activeItem = listRef.current.querySelector<HTMLLIElement>(
      `[data-index="${activeIndex}"]`
    );
    activeItem?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, isOpen, filtered]);

  const openDropdown = () => {
    if (disabled) return;
    setIsOpen(true);
    const currentIdx = filtered.indexOf(value);
    setActiveIndex(currentIdx >= 0 ? currentIdx : 0);
  };

  const selectCountry = (country: string) => {
    onChange(country);
    setQuery('');
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setQuery(newValue);
    onChange(newValue);
    if (!isOpen) {
      setIsOpen(true);
    }
    setActiveIndex(0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        openDropdown();
        return;
      }
      setActiveIndex((prev) => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      if (isOpen && filtered[activeIndex]) {
        e.preventDefault();
        selectCountry(filtered[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      if (isOpen) {
        e.preventDefault();
        setIsOpen(false);
        setQuery('');
      }
    } else if (e.key === 'Tab') {
      setIsOpen(false);
      setQuery('');
    }
  };

  const baseInputClasses =
    'border rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 px-4 py-2 text-base pr-10';

  const stateClasses = error
    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
    : success
    ? 'border-green-500 focus:border-green-500 focus:ring-green-500'
    : 'border-gray-300 focus:border-info-500 focus:ring-info-500';

  const disabledClasses = disabled
    ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
    : 'bg-gray-50 text-gray-900';

  const fullWidthClass = fullWidth ? 'w-full' : '';

  const displayValue = isOpen ? query : value;

  return (
    <div ref={containerRef} className={clsx('relative', fullWidthClass)}>
      {label && (
        <label
          htmlFor={inputId}
          className={clsx(
            'block text-sm font-medium mb-1',
            error ? 'text-red-700' : 'text-gray-700',
            disabled && 'text-gray-500'
          )}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        <input
          ref={inputRef}
          id={inputId}
          name={name}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            isOpen && filtered[activeIndex] ? `${inputId}-option-${activeIndex}` : undefined
          }
          autoComplete="country-name"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={openDropdown}
          onClick={openDropdown}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className={clsx(baseInputClasses, stateClasses, disabledClasses, fullWidthClass)}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <svg
            className={clsx(
              'w-5 h-5 text-gray-400 transition-transform',
              isOpen && 'rotate-180'
            )}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>

      {isOpen && (
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          className="absolute z-50 mt-1 w-full max-h-72 overflow-auto rounded-lg border border-gray-200 bg-gray-50 shadow-lg"
        >
          {filtered.length === 0 ? (
            <li className="px-4 py-2 text-sm text-gray-500">No countries found</li>
          ) : (
            filtered.map((country, idx) => {
              const isActive = idx === activeIndex;
              const isSelected = country === value;
              // A pinned item's divider shows below the last pinned entry, but only
              // when there is no active search query (filtered equals the full list).
              const showPinnedDivider =
                query.trim() === '' && idx === PINNED_COUNTRY_COUNT - 1;
              return (
                <li
                  key={country}
                  id={`${inputId}-option-${idx}`}
                  data-index={idx}
                  role="option"
                  aria-selected={isSelected}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectCountry(country);
                  }}
                  onMouseEnter={() => setActiveIndex(idx)}
                  className={clsx(
                    'px-4 py-2 text-sm cursor-pointer',
                    isActive ? 'bg-primary-50 text-primary-700' : 'text-gray-900',
                    isSelected && 'font-semibold',
                    showPinnedDivider && 'border-b border-gray-200'
                  )}
                >
                  {country}
                </li>
              );
            })
          )}
        </ul>
      )}

      {(error || helperText) && (
        <p className={clsx('mt-1 text-sm', error ? 'text-red-600' : 'text-gray-600')}>
          {error || helperText}
        </p>
      )}
    </div>
  );
};
